import {CustomEqualitySet} from "custom-equality-set";
import {Money} from "money";
import {AvdbType} from "../avdb-type";
import {Device} from "../device";
import {AvdbPricingAircraftBundle} from "./avdb-pricing-aircraft-bundle";
import {AvdbPricingBundle} from "./avdb-pricing-bundle";
import {AvdbPricingDeviceBundle} from "./avdb-pricing-device-bundle";
import {AvdbPricingProduct} from "./avdb-pricing-product";

/**
 * Bundle Package
 *
 * Within database pricing, this object represents a package containing one or more
 * bundles (PilotPaks, OnePaks, or device bundles) and optionally additional products
 * (subscriptions and single updates). These bundle packages are then ranked and
 * compared as a part of recommending packages in the pricing tool.
 */
export class AvdbPricingBundlePackage {
	rank = 0;
	value: Money;
	additionalProducts = new Map<Device, CustomEqualitySet<AvdbPricingProduct>>();

	constructor(public bundles: CustomEqualitySet<AvdbPricingBundle>) {}

	equals(otherPackage: AvdbPricingBundlePackage): boolean {
		return this.bundles.equals(otherPackage.bundles) && this.additionalProductsMatch(otherPackage);
	}

	private additionalProductsMatch(otherPackage: AvdbPricingBundlePackage): boolean {
		if (this.additionalProducts.size !== otherPackage.additionalProducts.size) {
			return false;
		}
		else {
			const devices = Array.from(this.additionalProducts.keys());
			for (let device of devices) {
				const products = this.additionalProducts.get(device);
				const otherProducts = otherPackage.additionalProducts.get(device);
				if (!products.equals(otherProducts))
					return false;
			}
			return true;
		}
	}

	increaseRank(amount: number) {
		this.rank += amount;
	}

	decreaseRank(amount: number) {
		this.rank -= amount;
	}

	get price(): Money {
		let sum = Money.sum(Array.from(this.bundles).map(bundle => bundle.price));
		if (this.additionalProducts.size === 0) {
			return sum;
		}
		else {
			for (let products of Array.from(this.additionalProducts.values())) {
				sum = Money.sum([sum, ...Array.from(products).map(product => product.price)]);
			}
			return sum;
		}
	}

	get formattedPercentSavings(): string {
		if (this.price && this.value && (this.price.value < this.value.value)) {
			const percentSavings = ((this.value.value - this.price.value) / this.value.value) * 100;
			return `${percentSavings.toFixed(0)}% Savings*`;
		}
		else {
			return "";
		}
	}

	get allAvdbTypes(): CustomEqualitySet<AvdbType> {
		let result = new CustomEqualitySet<AvdbType>();

		for (let bundle of Array.from(this.bundles)) {
			for (let avdbType of Array.from(bundle.allAvdbTypes)) {
				result.add(avdbType);
			}
		}

		for (let products of Array.from(this.additionalProducts.values())) {
			for (let product of Array.from(products)) {
				const avdbType = new AvdbType({
					id: product.productType.id,
					name: product.productType.name,
				});
				result.add(avdbType);
			}
		}

		return result;
	}

	get shouldDisplayIndividualPrices(): boolean {
		if (this.additionalProducts.size > 0) {
			return true;
		}
		else {
			return this.bundles.size > 1;
		}
	}

	static RANK_INCREASE_FOR_MAP_COVERAGE_REGION = 1;
	static RANK_DECREASE_FOR_EXTRA_MAP_COVERAGE_REGION: number = 0.05;

	getBundleThatCoversDevice(device: Device): AvdbPricingBundle {
		return Array.from(this.bundles).find(bundle => {
			if (bundle instanceof AvdbPricingDeviceBundle) {
				return (bundle as AvdbPricingDeviceBundle).deviceID === device.id;
			}
			else {
				return !!(bundle as AvdbPricingAircraftBundle).qualifyingDeviceModels
					.find(deviceModel => deviceModel.modelID === device.modelID);
			}
		});
	}

	getDevicesCoveredByAdditionalProducts(): Array<Device> {
		return Array.from(this.additionalProducts.keys());
	}

	getAdditionalProductsForDevice(device: Device): Array<AvdbPricingProduct> {
		const products = this.additionalProducts.get(device);
		return (products) ? Array.from(products) : [];
	}
}
